/* eslint-disable react-hooks/rules-of-hooks */
import {
    KratosTokens,
    PowerDie,
    PowerDieColor,
    PowerRollResult,
    RolledPowerDie,
    PowerDieResult,
    PowerRollInput,
    PowerReRollStrategy,
} from '../types';
import { Constants } from '../constants';
import { Utils } from '../utilities';

const MAX_DICE = 12;

type ReRollMode = 'normal' | 'black';

interface ReRollAssignment {
    dieIndex: number;
    mode: ReRollMode;
}

interface EvaluatedBundle {
    reRolls: ReRollAssignment[];
    woundProbability: number;
    expectedTotalPower: number;
    maxGain: number;
    riskedPower: number;
}

interface TableDieProfile {
    dieIndex: number;
    currentContribution: number;
    currentFacePower: number;
    dieKind: PowerDieColor | 'Unknown';
    normalExpectedValue: number;
    normalExpectedGain: number;
    normalUsefulGain: number;
    normalImproveChance: number;
    normalLossRisk: number;
    normalMaxGain: number;
    blackExpectedValue: number;
    blackExpectedGain: number;
    blackUsefulGain: number;
    blackImproveChance: number;
    blackLossRisk: number;
    blackMaxGain: number;
    blackLift: number;
    disposabilityScore: number;
}

type TablePosture = 'conservative' | 'balanced' | 'aggressive';

interface ReRollProjection {
    expectedValue: number;
    expectedGain: number;
    usefulGain: number;
    improveChance: number;
    lossRisk: number;
    maxGain: number;
}

const _emptyTokens = (): KratosTokens => ({ break: 0, black: 0, fire: 0, hope: 0, opening: 0, closing: 0, rouse: 0 });

const _poolResults: PowerDieResult[] = Array.from({ length: MAX_DICE }, () => ({
    calculatedPower: 0,
    usedTokens: _emptyTokens(),
    die: null as any,
}));

const _poolUsedRoll: { faces: never[]; currentFace: { power: number; potential: number; dot: number } }[] = Array.from(
    { length: MAX_DICE },
    () => ({
        faces: [] as never[],
        currentFace: { power: 0, potential: 0, dot: 0 },
    }),
);

const _poolUsedTokens: KratosTokens = _emptyTokens();

type AnnotatedResult = PowerDieResult & {
    dieIndex: number;
    indexWithBlack: number;
    indexWithoutBlack: number;
};

const _poolAnnotated: AnnotatedResult[] = Array.from({ length: MAX_DICE }, () => ({
    calculatedPower: 0,
    usedTokens: _emptyTokens(),
    die: null as any,
    dieIndex: 0,
    indexWithBlack: 0,
    indexWithoutBlack: 0,
}));
const _poolNewRoll: RolledPowerDie[] = new Array(MAX_DICE);

export function executePowerRoll(input: PowerRollInput): number {
    if (input.hits <= 0) return 0;

    const powerDiceColors = Utils.getPowerDiceColors(input.stats, input.hits, input.disableDiceLimits);
    const powerDice = Utils.getPowerDice(powerDiceColors);
    const allTokens = Utils.addKratosTokens(input.kratosPool, input.stats.autoTokens);
    const initialRoll = sortPowerRoll(powerDice.map(rollDie));
    const initialResult = calculatePowerRollResult(initialRoll, allTokens);
    let totalPower = calculateTotalPower(initialResult, input.stats.powerBonus);

    const canReRoll = allTokens.black + input.stats.powerReRolls > 0;
    if (totalPower < input.aeonTrespassValue && canReRoll && !input.disableReRolls) {
        const reRollResult = doPowerReRoll({
            initialRoll,
            initialResult,
            allTokens,
            aeonTrespassValue: input.aeonTrespassValue,
            powerReRolls: input.stats.powerReRolls,
            powerBonus: input.stats.powerBonus,
            reRollThreshold: input.reRollThreshold,
            strategy: input.reRollStrategy ?? Constants.DEFAULT_POWER_REROLL_STRATEGY,
        });
        totalPower = calculateTotalPower(reRollResult, input.stats.powerBonus);
    }

    return totalPower;
}

function doPowerReRoll(input: {
    initialRoll: RolledPowerDie[];
    initialResult: PowerRollResult;
    allTokens: KratosTokens;
    aeonTrespassValue: number;
    powerReRolls: number;
    powerBonus: number;
    reRollThreshold: number;
    strategy: PowerReRollStrategy;
}): PowerRollResult {
    if (input.strategy === PowerReRollStrategy.LEGACY_HEURISTIC) {
        return resolveLegacyHeuristicReRoll(input.initialResult, input.powerReRolls, input.reRollThreshold);
    }

    const reRolls = selectReRollBundle(input);
    if (reRolls.length === 0) {
        return input.initialResult;
    }

    const rolledDice = input.initialRoll.map((die) => die);
    reRolls.forEach((reRoll) => {
        const rerolledDie = rollDie(input.initialRoll[reRoll.dieIndex]);
        rolledDice[reRoll.dieIndex] = reRoll.mode === 'black' ? applyBlackToken(rerolledDie) : rerolledDie;
    });

    return calculatePowerRollResult(sortPowerRoll(rolledDice), input.allTokens);
}

function resolveLegacyHeuristicReRoll(
    rollResult: PowerRollResult,
    powerReRolls: number,
    reRollThreshold: number,
): PowerRollResult {
    const len = rollResult.dieResults.length;
    let availableTokens = Utils.shallowCopy(rollResult.remainingTokens);

    for (let i = 0; i < len; i++) {
        const result = rollResult.dieResults[i];
        _poolAnnotated[i].calculatedPower = result.calculatedPower;
        _poolAnnotated[i].usedTokens = result.usedTokens;
        _poolAnnotated[i].die = result.die;
        _poolAnnotated[i].dieIndex = i;
        _poolAnnotated[i].indexWithBlack = calculateReRollIndex(result, rollResult.remainingTokens, true);
        _poolAnnotated[i].indexWithoutBlack = calculateReRollIndex(result, rollResult.remainingTokens, false);
    }

    const annotatedSlice = _poolAnnotated.slice(0, len);
    annotatedSlice.sort((a, b) => {
        if (a.indexWithBlack !== b.indexWithBlack) return b.indexWithBlack - a.indexWithBlack;
        return b.indexWithoutBlack - a.indexWithoutBlack;
    });

    for (let index = 0; index < len; index++) {
        const result = annotatedSlice[index];
        const isWorstDie = index === 0;
        const shouldReRollWithBlack = result.indexWithBlack > reRollThreshold;
        const shouldReRollWithoutBlack = result.indexWithoutBlack > reRollThreshold;

        if (availableTokens.black > 0 && (shouldReRollWithBlack || isWorstDie)) {
            availableTokens.black--;
            _poolNewRoll[index] = applyBlackToken(rollDie(result.die));
        } else if (powerReRolls > 0 && (shouldReRollWithoutBlack || isWorstDie)) {
            powerReRolls--;
            _poolNewRoll[index] = rollDie(result.die);
        } else {
            availableTokens = Utils.addKratosTokens(availableTokens, result.usedTokens);
            _poolNewRoll[index] = result.die;
        }
    }

    return calculatePowerRollResult(_poolNewRoll.slice(0, len), availableTokens);
}

function selectReRollBundle(input: {
    initialRoll: RolledPowerDie[];
    initialResult: PowerRollResult;
    allTokens: KratosTokens;
    aeonTrespassValue: number;
    powerReRolls: number;
    powerBonus: number;
    reRollThreshold: number;
    strategy: PowerReRollStrategy;
}): ReRollAssignment[] {
    switch (input.strategy) {
        case PowerReRollStrategy.TABLE_HEURISTIC:
            return selectTableStrategyBundle(input);
        case PowerReRollStrategy.OPTIMAL_STRATEGY:
            return selectOptimalStrategyBundle(input);
        case PowerReRollStrategy.LEGACY_HEURISTIC:
        default:
            return selectLegacyHeuristicBundle(input.initialResult, input.powerReRolls, input.reRollThreshold);
    }
}

function selectLegacyHeuristicBundle(
    rollResult: PowerRollResult,
    powerReRolls: number,
    reRollThreshold: number,
): ReRollAssignment[] {
    const len = rollResult.dieResults.length;
    let availableBlack = rollResult.remainingTokens.black;
    const reRolls: ReRollAssignment[] = [];

    for (let i = 0; i < len; i++) {
        const result = rollResult.dieResults[i];
        _poolAnnotated[i].calculatedPower = result.calculatedPower;
        _poolAnnotated[i].usedTokens = result.usedTokens;
        _poolAnnotated[i].die = result.die;
        _poolAnnotated[i].dieIndex = i;
        _poolAnnotated[i].indexWithBlack = calculateReRollIndex(result, rollResult.remainingTokens, true);
        _poolAnnotated[i].indexWithoutBlack = calculateReRollIndex(result, rollResult.remainingTokens, false);
    }

    const annotatedSlice = _poolAnnotated.slice(0, len);
    annotatedSlice.sort((a, b) => {
        if (a.indexWithBlack !== b.indexWithBlack) return b.indexWithBlack - a.indexWithBlack;
        if (a.indexWithoutBlack !== b.indexWithoutBlack) return b.indexWithoutBlack - a.indexWithoutBlack;
        return a.dieIndex - b.dieIndex;
    });

    for (let index = 0; index < len; index++) {
        const result = annotatedSlice[index];
        const isWorstDie = index === 0;
        const shouldReRollWithBlack = result.indexWithBlack > reRollThreshold;
        const shouldReRollWithoutBlack = result.indexWithoutBlack > reRollThreshold;

        if (availableBlack > 0 && (shouldReRollWithBlack || isWorstDie)) {
            availableBlack--;
            reRolls.push({ dieIndex: result.dieIndex, mode: 'black' });
        } else if (powerReRolls > 0 && (shouldReRollWithoutBlack || isWorstDie)) {
            powerReRolls--;
            reRolls.push({ dieIndex: result.dieIndex, mode: 'normal' });
        }
    }

    return sortBundle(reRolls);
}

function selectTableStrategyBundle(input: {
    initialRoll: RolledPowerDie[];
    initialResult: PowerRollResult;
    allTokens: KratosTokens;
    aeonTrespassValue: number;
    powerReRolls: number;
    powerBonus: number;
}): ReRollAssignment[] {
    const currentTotal = calculateTotalPower(input.initialResult, input.powerBonus);
    const shortfall = Math.max(input.aeonTrespassValue - currentTotal, 0);
    if (shortfall <= 0) return [];

    const maxBlack = input.allTokens.black;
    const maxNormal = input.powerReRolls;
    const maxRerolls = Math.min(input.initialRoll.length, maxBlack + maxNormal);
    if (maxRerolls <= 0) return [];

    const profiles = buildSimpleTableProfiles(input.initialRoll, input.initialResult, shortfall);
    const posture = getTablePosture(shortfall, maxRerolls);
    const selectedTargets = selectTableTargetsByPosture(profiles, posture, maxRerolls);
    return assignBlackModes(selectedTargets, maxBlack);
}

function buildSimpleTableProfiles(
    initialRoll: RolledPowerDie[],
    initialResult: PowerRollResult,
    shortfall: number,
): TableDieProfile[] {
    return initialResult.dieResults.map((result, dieIndex) => {
        const dieKind = getPowerDieColor(initialRoll[dieIndex]);
        const tokensForDie = Utils.addKratosTokens(initialResult.remainingTokens, result.usedTokens);
        const normalProjection = projectReRoll(result, tokensForDie, shortfall, false);
        const blackProjection = projectReRoll(result, tokensForDie, shortfall, true);

        return {
            dieIndex,
            currentContribution: result.calculatedPower,
            currentFacePower: result.die.currentFace.power,
            dieKind,
            normalExpectedValue: normalProjection.expectedValue,
            normalExpectedGain: normalProjection.expectedGain,
            normalUsefulGain: normalProjection.usefulGain,
            normalImproveChance: normalProjection.improveChance,
            normalLossRisk: normalProjection.lossRisk,
            normalMaxGain: normalProjection.maxGain,
            blackExpectedValue: blackProjection.expectedValue,
            blackExpectedGain: blackProjection.expectedGain,
            blackUsefulGain: blackProjection.usefulGain,
            blackImproveChance: blackProjection.improveChance,
            blackLossRisk: blackProjection.lossRisk,
            blackMaxGain: blackProjection.maxGain,
            blackLift: blackProjection.expectedValue - normalProjection.expectedValue,
            disposabilityScore: calculateTableDisposabilityScore(result, normalProjection, blackProjection),
        };
    });
}

function getTablePosture(shortfall: number, maxRerolls: number): TablePosture {
    const pressure = shortfall / Math.max(maxRerolls, 1);
    if (pressure <= 0.75) return 'conservative';
    if (pressure <= 1.5) return 'balanced';
    return 'aggressive';
}

function selectTableTargetsByPosture(
    profiles: TableDieProfile[],
    posture: TablePosture,
    maxRerolls: number,
): TableDieProfile[] {
    if (maxRerolls <= 0 || profiles.length === 0) return [];

    const sortedProfiles = sortTableProfiles(profiles);
    const zeroContribution = sortedProfiles.filter((profile) => profile.currentContribution === 0);
    const oneContribution = sortedProfiles.filter((profile) => profile.currentContribution === 1);

    switch (posture) {
        case 'conservative': {
            if (zeroContribution.length > 0) {
                return zeroContribution.slice(0, maxRerolls);
            }

            const lowRiskChoices = sortedProfiles.filter(
                (profile) => profile.currentContribution < 2 && profile.normalLossRisk <= 0.5,
            );
            return (lowRiskChoices[0] ? [lowRiskChoices[0]] : [sortedProfiles[0]]).slice(0, maxRerolls);
        }
        case 'balanced': {
            const selected: TableDieProfile[] = zeroContribution.slice(0, maxRerolls);
            const pushUnique = (profile?: TableDieProfile) => {
                if (profile && !selected.some((selectedProfile) => selectedProfile.dieIndex === profile.dieIndex)) {
                    selected.push(profile);
                }
            };

            oneContribution.forEach((profile) => {
                if (selected.length < maxRerolls) {
                    pushUnique(profile);
                }
            });

            if (selected.length < maxRerolls) {
                const swingTwo = sortedProfiles.find(
                    (profile) =>
                        profile.currentContribution === 2 &&
                        profile.normalUsefulGain >= 1 &&
                        profile.normalExpectedGain >= 0 &&
                        profile.normalLossRisk <= 1,
                );
                pushUnique(swingTwo);
            }

            return selected.length > 0 ? selected.slice(0, maxRerolls) : [sortedProfiles[0]].slice(0, maxRerolls);
        }
        case 'aggressive':
        default: {
            return sortedProfiles
                .filter((profile, index) => {
                    if (profile.currentContribution <= 1) return true;
                    if (index < maxRerolls) return true;
                    return profile.normalUsefulGain > profile.normalLossRisk;
                })
                .slice(0, maxRerolls);
        }
    }
}

function assignBlackModes(selectedTargets: TableDieProfile[], maxBlack: number): ReRollAssignment[] {
    if (selectedTargets.length === 0) return [];

    const promoted = new Set(
        [...selectedTargets]
            .sort((a, b) => {
                if (a.blackLift !== b.blackLift) return b.blackLift - a.blackLift;
                if (a.blackUsefulGain !== b.blackUsefulGain) return b.blackUsefulGain - a.blackUsefulGain;
                if (a.blackImproveChance !== b.blackImproveChance) return b.blackImproveChance - a.blackImproveChance;
                if (a.currentContribution !== b.currentContribution) return a.currentContribution - b.currentContribution;
                return a.dieIndex - b.dieIndex;
            })
            .slice(0, Math.min(maxBlack, selectedTargets.length))
            .map((profile) => profile.dieIndex),
    );

    return sortBundle(
        selectedTargets.map((profile) => ({
            dieIndex: profile.dieIndex,
            mode: promoted.has(profile.dieIndex) ? 'black' : 'normal',
        })),
    );
}

function sortTableProfiles(profiles: TableDieProfile[]): TableDieProfile[] {
    return [...profiles].sort((a, b) => {
        if (a.disposabilityScore !== b.disposabilityScore) return b.disposabilityScore - a.disposabilityScore;
        if (a.currentContribution !== b.currentContribution) return a.currentContribution - b.currentContribution;
        if (a.normalUsefulGain !== b.normalUsefulGain) return b.normalUsefulGain - a.normalUsefulGain;
        if (a.normalImproveChance !== b.normalImproveChance) return b.normalImproveChance - a.normalImproveChance;
        if (a.currentFacePower !== b.currentFacePower) return a.currentFacePower - b.currentFacePower;
        return a.dieIndex - b.dieIndex;
    });
}

function getPowerDieColor(die: PowerDie): PowerDieColor | 'Unknown' {
    const signature = getDieSignature(die);
    if (signature === getDieSignature(Constants.RED_POWER_DIE)) return PowerDieColor.RED;
    if (signature === getDieSignature(Constants.BLACK_POWER_DIE)) return PowerDieColor.BLACK;
    if (signature === getDieSignature(Constants.WHITE_POWER_DIE)) return PowerDieColor.WHITE;
    if (signature === getDieSignature(Constants.MORTAL_POWER_DIE)) return PowerDieColor.MORTAL;
    return 'Unknown';
}

function getDieSignature(die: PowerDie): string {
    return die.faces.map((face) => `${face.power}/${face.potential}/${face.dot}`).join('|');
}

function projectReRoll(
    dieResult: PowerDieResult,
    tokens: Required<KratosTokens>,
    shortfall: number,
    usingBlack: boolean,
): ReRollProjection {
    const currentContribution = dieResult.calculatedPower;
    let totalValue = 0;
    let totalUsefulGain = 0;
    let totalLoss = 0;
    let improveOutcomes = 0;
    let maxGain = Number.NEGATIVE_INFINITY;

    dieResult.die.faces.forEach((face) => {
        const evaluatedFace = usingBlack && face.potential > 0
            ? { power: face.power + 1, potential: face.potential - 1, dot: face.dot }
            : face;
        const nextValue = calculateSingleDiePower(evaluatedFace, tokens.hope, tokens.break, tokens.fire);
        totalValue += nextValue;

        const gain = nextValue - currentContribution;
        if (gain > 0) {
            improveOutcomes++;
            totalUsefulGain += Math.min(gain, Math.max(shortfall, 1));
        } else if (gain < 0) {
            totalLoss += Math.min(-gain, Math.max(currentContribution, 1));
        }

        maxGain = Math.max(maxGain, gain);
    });

    const faceCount = dieResult.die.faces.length;
    const expectedValue = totalValue / faceCount;

    return {
        expectedValue,
        expectedGain: expectedValue - currentContribution,
        usefulGain: totalUsefulGain / faceCount,
        improveChance: improveOutcomes / faceCount,
        lossRisk: totalLoss / faceCount,
        maxGain,
    };
}

function calculateTableDisposabilityScore(
    dieResult: PowerDieResult,
    normalProjection: ReRollProjection,
    blackProjection: ReRollProjection,
): number {
    return (
        normalProjection.usefulGain * 50 +
        normalProjection.expectedGain * 30 +
        normalProjection.improveChance * 20 +
        blackProjection.usefulGain * 10 -
        normalProjection.lossRisk * 30 -
        dieResult.calculatedPower * 100 -
        dieResult.die.currentFace.power * 5
    );
}

function selectOptimalStrategyBundle(input: {
    initialRoll: RolledPowerDie[];
    initialResult: PowerRollResult;
    allTokens: KratosTokens;
    aeonTrespassValue: number;
    powerReRolls: number;
    powerBonus: number;
}): ReRollAssignment[] {
    const currentTotal = calculateTotalPower(input.initialResult, input.powerBonus);
    const bundles = generateAllLegalBundles(input.initialRoll.length, input.allTokens.black, input.powerReRolls);
    let bestBundle: EvaluatedBundle | null = null;
    for (const bundle of bundles) {
        const evaluated = evaluateBundle(
            input.initialRoll,
            input.allTokens,
            bundle,
            input.powerBonus,
            input.aeonTrespassValue,
            currentTotal,
            input.initialResult,
        );

        if (!bestBundle || compareOptimalBundles(evaluated, bestBundle) < 0) {
            bestBundle = evaluated;
        }
    }

    return bestBundle?.reRolls ?? [];
}

function generateAllLegalBundles(diceCount: number, maxBlack: number, maxNormal: number): ReRollAssignment[][] {
    const bundles: ReRollAssignment[][] = [[]];
    const currentAssignments: ReRollAssignment[] = [];

    const visit = (dieIndex: number, blackUsed: number, normalUsed: number) => {
        if (dieIndex >= diceCount) {
            if (currentAssignments.length > 0) {
                bundles.push(sortBundle([...currentAssignments]));
            }
            return;
        }

        visit(dieIndex + 1, blackUsed, normalUsed);

        if (normalUsed < maxNormal) {
            currentAssignments.push({ dieIndex, mode: 'normal' });
            visit(dieIndex + 1, blackUsed, normalUsed + 1);
            currentAssignments.pop();
        }

        if (blackUsed < maxBlack) {
            currentAssignments.push({ dieIndex, mode: 'black' });
            visit(dieIndex + 1, blackUsed + 1, normalUsed);
            currentAssignments.pop();
        }
    };

    visit(0, 0, 0);
    return bundles;
}

function evaluateBundle(
    initialRoll: RolledPowerDie[],
    allTokens: KratosTokens,
    reRolls: ReRollAssignment[],
    powerBonus: number,
    targetPower: number,
    currentTotal: number,
    currentResult: PowerRollResult,
): EvaluatedBundle {
    if (reRolls.length === 0) {
        return {
            reRolls,
            woundProbability: currentTotal >= targetPower ? 1 : 0,
            expectedTotalPower: currentTotal,
            maxGain: 0,
            riskedPower: 0,
        };
    }

    const workingRoll = initialRoll.map((die) => die);
    const rerollEntries = sortBundle(reRolls);
    let successfulOutcomes = 0;
    let totalPowerAcrossOutcomes = 0;
    let maxGain = Number.NEGATIVE_INFINITY;
    const totalOutcomes = Math.pow(6, rerollEntries.length);

    const visit = (entryIndex: number) => {
        if (entryIndex >= rerollEntries.length) {
            const result = calculatePowerRollResult(sortPowerRoll([...workingRoll]), allTokens);
            const totalPower = calculateTotalPower(result, powerBonus);
            totalPowerAcrossOutcomes += totalPower;
            if (totalPower >= targetPower) {
                successfulOutcomes++;
            }
            maxGain = Math.max(maxGain, totalPower - currentTotal);
            return;
        }

        const entry = rerollEntries[entryIndex];
        const sourceDie = initialRoll[entry.dieIndex];
        sourceDie.faces.forEach((face) => {
            const rerolledDie = {
                faces: sourceDie.faces,
                currentFace: face,
            };
            workingRoll[entry.dieIndex] = entry.mode === 'black' ? applyBlackToken(rerolledDie) : rerolledDie;
            visit(entryIndex + 1);
        });
        workingRoll[entry.dieIndex] = initialRoll[entry.dieIndex];
    };

    visit(0);

    return {
        reRolls: rerollEntries,
        woundProbability: successfulOutcomes / totalOutcomes,
        expectedTotalPower: totalPowerAcrossOutcomes / totalOutcomes,
        maxGain,
        riskedPower: rerollEntries.reduce(
            (total, reRoll) => total + (currentResult.dieResults[reRoll.dieIndex]?.calculatedPower ?? 0),
            0,
        ),
    };
}

function compareOptimalBundles(a: EvaluatedBundle, b: EvaluatedBundle): number {
    if (a.woundProbability !== b.woundProbability) return b.woundProbability > a.woundProbability ? 1 : -1;
    return compareBundlesByTieBreak(a.reRolls, b.reRolls);
}

function compareBundlesByTieBreak(a: ReRollAssignment[], b: ReRollAssignment[]): number {
    if (a.length !== b.length) return a.length - b.length;

    const blackCountA = a.filter((reRoll) => reRoll.mode === 'black').length;
    const blackCountB = b.filter((reRoll) => reRoll.mode === 'black').length;
    if (blackCountA !== blackCountB) return blackCountA - blackCountB;

    const signatureA = getBundleSignature(a);
    const signatureB = getBundleSignature(b);
    return signatureA.localeCompare(signatureB);
}

function getBundleSignature(bundle: ReRollAssignment[]): string {
    return sortBundle(bundle)
        .map((reRoll) => `${reRoll.dieIndex}:${reRoll.mode === 'black' ? '1' : '0'}`)
        .join('|');
}

function sortBundle(bundle: ReRollAssignment[]): ReRollAssignment[] {
    return [...bundle].sort((a, b) => {
        if (a.dieIndex !== b.dieIndex) return a.dieIndex - b.dieIndex;
        if (a.mode === b.mode) return 0;
        return a.mode === 'normal' ? -1 : 1;
    });
}

function applyBlackToken(die: RolledPowerDie): RolledPowerDie {
    const newFaces = die.faces.map((face) =>
        face.potential > 0
            ? { power: face.power + 1, potential: face.potential - 1, dot: face.dot }
            : { power: face.power, potential: face.potential, dot: face.dot },
    );
    const faceIndex = die.faces.indexOf(die.currentFace);
    return {
        faces: newFaces,
        currentFace: newFaces[faceIndex],
    };
}

function calculateReRollIndex(dieResult: PowerDieResult, tokens: Required<KratosTokens>, usingBlack?: boolean): number {
    const usedTokens = dieResult.usedTokens;
    const hope = tokens.hope + usedTokens.hope;
    const breakTokens = tokens.break + usedTokens.break + (usingBlack ? 1 : 0);
    const fire = tokens.fire + usedTokens.fire;

    let numberOfWorseRolls = 0;
    const die = dieResult.die;
    for (let i = 0; i < die.faces.length; i++) {
        const power = calculateSingleDiePower(die.faces[i], hope, breakTokens, fire);
        if (power < dieResult.calculatedPower) {
            numberOfWorseRolls++;
        }
    }
    return 1 - numberOfWorseRolls / die.faces.length;
}

function calculateSingleDiePower(
    face: { power: number; potential: number; dot: number },
    hope: number,
    breakTokens: number,
    fire: number,
): number {
    let power = face.power;

    const hopeForDots = Math.min(face.dot, hope);
    power += hopeForDots;

    let potentialRemaining = face.potential;
    const breakUsed = Math.min(potentialRemaining, breakTokens);
    power += breakUsed;
    potentialRemaining -= breakUsed;

    const fireUsed = Math.min(potentialRemaining, fire);
    power += fireUsed;
    potentialRemaining -= fireUsed;

    power += Math.min(potentialRemaining, hope - hopeForDots);

    return power;
}

function rollDie(die: PowerDie): RolledPowerDie {
    return {
        ...die,
        currentFace: die.faces[Math.floor(Math.random() * die.faces.length)],
    };
}

function sortPowerRoll(powerRoll: RolledPowerDie[]): RolledPowerDie[] {
    powerRoll.sort((a, b) => {
        const aFace = a.currentFace;
        const bFace = b.currentFace;
        if (aFace.power !== bFace.power) return bFace.power - aFace.power;
        if (aFace.potential !== bFace.potential) return bFace.potential - aFace.potential;
        return bFace.dot - aFace.dot;
    });
    return powerRoll;
}

function calculatePowerRollResult(powerRoll: RolledPowerDie[], tokens: Required<KratosTokens>): PowerRollResult {
    const len = powerRoll.length;

    for (let i = 0; i < len; i++) {
        _poolResults[i].calculatedPower = 0;
        _poolResults[i].die = powerRoll[i];
        const usedTokens = _poolResults[i].usedTokens;
        usedTokens.break = 0;
        usedTokens.black = 0;
        usedTokens.fire = 0;
        usedTokens.hope = 0;
        usedTokens.opening = 0;
        usedTokens.closing = 0;
        usedTokens.rouse = 0;

        const usedRoll = _poolUsedRoll[i].currentFace;
        usedRoll.power = 0;
        usedRoll.potential = 0;
        usedRoll.dot = 0;
    }
    _poolUsedTokens.break = 0;
    _poolUsedTokens.black = 0;
    _poolUsedTokens.fire = 0;
    _poolUsedTokens.hope = 0;
    _poolUsedTokens.opening = 0;
    _poolUsedTokens.closing = 0;
    _poolUsedTokens.rouse = 0;

    addInitialPower(powerRoll, _poolUsedRoll, _poolResults, len);
    useTokensForPower('dot', tokens, _poolUsedTokens, powerRoll, _poolUsedRoll, _poolResults, 'hope', len);
    useTokensForPower('potential', tokens, _poolUsedTokens, powerRoll, _poolUsedRoll, _poolResults, 'break', len);
    useTokensForPower('potential', tokens, _poolUsedTokens, powerRoll, _poolUsedRoll, _poolResults, 'fire', len);
    useTokensForPower('potential', tokens, _poolUsedTokens, powerRoll, _poolUsedRoll, _poolResults, 'hope', len);

    const dieResults: PowerDieResult[] = new Array(len);
    for (let i = 0; i < len; i++) {
        dieResults[i] = {
            calculatedPower: _poolResults[i].calculatedPower,
            usedTokens: { ..._poolResults[i].usedTokens },
            die: _poolResults[i].die,
        };
    }

    return {
        dieResults,
        remainingTokens: Utils.subtractKratosTokens(tokens, _poolUsedTokens),
    };
}

function addInitialPower(
    powerRoll: RolledPowerDie[],
    powerRollUsed: { faces: never[]; currentFace: { power: number; potential: number; dot: number } }[],
    dieResults: PowerDieResult[],
    len: number,
): void {
    for (let i = 0; i < len; i++) {
        const power = powerRoll[i].currentFace.power;
        powerRollUsed[i].currentFace.power += power;
        dieResults[i].calculatedPower += power;
    }
}

function useTokensForPower(
    attribute: 'dot' | 'potential',
    availableTokens: Required<KratosTokens>,
    usedTokens: Required<KratosTokens>,
    powerRoll: RolledPowerDie[],
    powerRollUsed: { faces: never[]; currentFace: { power: number; potential: number; dot: number } }[],
    dieResults: PowerDieResult[],
    tokenType: keyof KratosTokens,
    len: number,
): void {
    for (let i = 0; i < len && usedTokens[tokenType] < availableTokens[tokenType]; i++) {
        const usableTokens = Math.max(powerRoll[i].currentFace[attribute] - powerRollUsed[i].currentFace[attribute], 0);
        const tokensToConvert = Math.min(usableTokens, availableTokens[tokenType] - usedTokens[tokenType]);

        powerRollUsed[i].currentFace[attribute] += tokensToConvert;
        usedTokens[tokenType] += tokensToConvert;
        dieResults[i].usedTokens[tokenType] += tokensToConvert;
        dieResults[i].calculatedPower += tokensToConvert;
    }
}

function calculateTotalPower(result: PowerRollResult, bonusPower: number = 0): number {
    let totalPower = bonusPower;
    result.dieResults.forEach((dieResult) => {
        totalPower += dieResult.calculatedPower;
    });
    return totalPower;
}
